import { html, nothing } from 'lit';
import { ago, dateOnly, number } from '../../shared/ui/format.js';
import { overview, takeSeat } from '../api/developer.js';
import { PROFILE_PATH, accountPath, isPersonal, stateVariant, word } from '../accounts.js';
import { AccountView } from './account-view.js';
import '../ui/getting-started.js';

/**
 * One team's home.
 *
 * Until something has run, it is the getting-started page - the three steps that make the first run
 * happen - and nothing else, because nothing else has anything to show. After that it is where the
 * team stands, as four figures that each lead to their own page, and the one warning that matters if
 * the team is over quota. An owner who holds no seat takes one from here, without an invitation,
 * while there is one free.
 *
 * <b>A member sees it too</b>, which is what makes it the one page everybody in the team shares. The
 * API answers the owner's view only to an owner; for a member the page falls back to what their own
 * overview says about this team - the plan, whether it is over quota, and their seat - and says who
 * runs the team.
 *
 * <b>A personal team has no seats</b>, so none of the figures apply. Its home says what personal use
 * is and where the lock stands - which the person's own overview knows and the team's does not - and
 * then the repositories and tokens, the two things a personal team has.
 */
export class UserAccountOverview extends AccountView
{
   static section = 'overview';

   static properties = { mine: { state: true }, me: { state: true } };

   constructor()
   {
      super();
      this.mine = null;
      this.me = null;
   }

   async load(signal)
   {
      await super.load(signal);

      // The member's view of the same team: what their own overview says about it.
      if (this.missing && this.account)
      {
         const me = await overview(signal).catch(() => null);

         this.mine = (me?.accounts ?? []).find(entry => entry.id === this.account) ?? null;
      }

      // The lock is the person's, not the team's: the same request, read for a different field.
      if (isPersonal(this.team))
      {
         this.me = await overview(signal).catch(() => null);
      }
   }

   /** Nothing has run yet: no repository has been named by a run. */
   get fresh()
   {
      // Nothing has run: no repository listed and nobody holding a seat. A team that only ever runs
      // locally against a self-hosted remote never lists a repository, so seats are the second sign of life.
      return Boolean(this.team)
         && (this.team.projects ?? []).length === 0
         && !(this.team.members ?? []).some(member => member.state === 'active');
   }

   /** Whether the owner reading this holds no seat here and there is one free to take. */
   get seatToTake()
   {
      const usage = this.team?.usage ?? {};
      const allowed = (usage.seatQuota ?? 0) + (usage.includedFreeSeats ?? 0);
      const held = (this.team?.members ?? []).some(member => member.you && member.state === 'active');

      return Boolean(this.team) && !isPersonal(this.team) && !held && (usage.countingSeats ?? 0) < allowed;
   }

   get pageActions()
   {
      return this.seatToTake
         ? [{ id: 'seat', label: 'Take a seat', icon: 'fa-solid fa-chair', variant: 'primary', disabled: this.busy }]
         : [];
   }

   onPageAction(id)
   {
      if (id === 'seat')
      {
         void this.act(() => takeSeat(this.account), 'You hold a seat in this team now. A covdbg login for this team is covered.');
      }
   }

   get content()
   {
      // A member: not the owner's view, but not "not yours" either.
      if (this.missing && this.mine)
      {
         return this.memberView;
      }

      return super.content;
   }

   /** A member's heading says the same as an owner's, from what their own overview knows. */
   get description()
   {
      const mine = this.mine;

      if (this.team || !mine)
      {
         return super.description;
      }

      const allowed = (mine.seatQuota ?? 0) + (mine.includedFreeSeats ?? 0);

      return `${mine.name ?? 'Team'} · ${word(mine.plan)} plan · ${number(mine.countingSeats ?? 0)} of ${number(allowed)} seats`;
   }

   get body()
   {
      if (isPersonal(this.team))
      {
         return html`
            ${this.personalUse}
            ${this.fresh ? this.gettingStarted : this.personalStats}`;
      }

      if (this.fresh)
      {
         return this.gettingStarted;
      }

      const usage = this.team.usage ?? {};

      return html`
         ${usage.overQuota ? this.overQuota : nothing}
         ${this.stats}`;
   }

   get gettingStarted()
   {
      return html`
         <user-getting-started
            .account=${this.account}
            .name=${this.team.name ?? ''}
            ?personal=${isPersonal(this.team)}
            owner
         ></user-getting-started>`;
   }

   /** What personal use is, in two sentences, and where the lock stands for this person. */
   get personalUse()
   {
      const me = this.me;
      const lock = me?.lock;
      const seatTeams = (me?.accounts ?? [])
         .filter(account => !account.personal && account.seat?.state === 'active')
         .map(account => account.name);

      return html`
         <lia-card title="Personal use" icon="fa-solid fa-user">
            <p class=${me ? 'mb-2' : 'mb-0'}>
               Public repositories are free, without limit and wherever they live. Private ones are one
               at a time: the first private repository you run covdbg in is locked to you until you
               release it from <a href=${PROFILE_PATH}>your profile</a>.
            </p>
            ${me
               ? html`
                  <p class="mb-0 text-body-secondary">
                     ${seatTeams.length
                        ? `You hold a seat in ${seatTeams.join(', ')}; it covers a covdbg login for ${seatTeams.length === 1 ? 'that team' : 'those teams'} only. `
                        : nothing}
                     ${lock
                        ? html`This personal team is locked to <code>${lock.remoteKey}</code>${lock.lockedAt ? ` since ${dateOnly(lock.lockedAt)}` : ''}.`
                        : 'This personal team is not locked to a repository yet.'}
                  </p>`
               : nothing}
         </lia-card>`;
   }

   /** The two things a personal team has, each leading to its page. */
   get personalStats()
   {
      const projects = this.team.projects ?? [];
      const tokens = (this.team.tokens ?? []).filter(token => !token.revokedAt);

      return html`
         <lia-stat-row
            .columns=${2}
            .items=${[
               {
                  label: 'Repositories',
                  value: number(projects.length),
                  icon: 'fa-brands fa-git-alt',
                  description: `${number(projects.filter(project => project.visibility === 'public').length)} public`,
                  href: accountPath('repositories', this.account)
               },
               {
                  label: 'Project tokens',
                  value: number(tokens.length),
                  icon: 'fa-solid fa-key',
                  description: tokens.length === 1 ? 'one CI credential' : 'CI credentials',
                  href: accountPath('tokens', this.account)
               }
            ]}
         ></lia-stat-row>`;
   }

   get overQuota()
   {
      const usage = this.team.usage ?? {};

      return html`
         <lia-alert
            variant="warning"
            icon="fa-solid fa-triangle-exclamation"
            heading="Over quota"
            message=${`${number(usage.billable)} billable seat(s) against ${number(usage.seatQuota)} paid for. `
               + 'Runs still succeed; after the grace period, reports are held back until seats are bought '
               + 'or members removed.'}
            .actions=${[{ id: 'billing', label: 'Buy seats', variant: 'warning', size: 'sm', href: accountPath('billing', this.account) }]}
         ></lia-alert>`;
   }

   /** Where the team stands, as the four figures the plan is made of. */
   get stats()
   {
      const usage = this.team.usage ?? {};
      const allowed = (usage.seatQuota ?? 0) + (usage.includedFreeSeats ?? 0);

      return html`
         <lia-stat-row
            .columns=${4}
            .items=${[
               {
                  label: 'Seats in use',
                  value: `${number(usage.countingSeats ?? 0)} of ${number(allowed)}`,
                  icon: 'fa-solid fa-chair',
                  variant: usage.overQuota ? 'warning' : 'success',
                  description: `${number(usage.pendingSeats ?? 0)} invited · ${number((this.team.unseatedAuthors ?? []).length)} author(s) without a seat`,
                  href: accountPath('members', this.account)
               },
               {
                  label: 'Plan',
                  value: word(this.team.plan),
                  icon: 'fa-solid fa-file-invoice',
                  description: `${number(usage.seatQuota ?? 0)} paid, ${number(usage.includedFreeSeats ?? 0)} free`,
                  href: accountPath('billing', this.account)
               },
               {
                  label: 'Repositories',
                  value: number((this.team.projects ?? []).length),
                  icon: 'fa-brands fa-git-alt',
                  description: `${number((this.team.projects ?? []).filter(project => project.visibility === 'public').length)} public · ${number((this.team.tokens ?? []).filter(token => !token.revokedAt).length)} token(s)`,
                  href: accountPath('repositories', this.account)
               },
               {
                  label: 'Owners',
                  value: number((this.team.owners ?? []).length),
                  icon: 'fa-solid fa-user-shield',
                  description: (this.team.owners ?? []).map(owner => owner.displayName ?? owner.email).slice(0, 2).join(', '),
                  href: accountPath('members/owners', this.account)
               }
            ]}
         ></lia-stat-row>`;
   }

   /** What a member is told: their seat, the plan, and who to ask. */
   get memberView()
   {
      const mine = this.mine;
      const seat = mine.seat;
      const allowed = (mine.seatQuota ?? 0) + (mine.includedFreeSeats ?? 0);

      return html`
         ${mine.overQuota
            ? html`
               <lia-alert
                  variant="warning"
                  icon="fa-solid fa-triangle-exclamation"
                  heading="This team is over quota"
                  message="Runs still succeed. After the grace period, reports are held back until an owner buys
                     seats or removes members."
               ></lia-alert>`
            : nothing}
         <div class="row g-3">
            <div class="col-lg-6">
               <lia-card title="Your seat" icon="fa-solid fa-chair">
                  <div class="d-flex align-items-center gap-2 mb-3">
                     ${seat
                        ? html`<lia-badge variant=${stateVariant(seat.state)}>${word(seat.state)}</lia-badge>
                           <span class="text-body-secondary small">
                              ${seat.lastSeenAt ? `last run ${ago(seat.lastSeenAt)}` : 'no run yet'}
                           </span>`
                        : html`<span class="text-body-secondary">You hold no seat here.</span>`}
                  </div>
                  <dl class="row small mb-0">
                     <dt class="col-6 fw-normal text-body-secondary">Plan</dt>
                     <dd class="col-6">${word(mine.plan)}</dd>
                     <dt class="col-6 fw-normal text-body-secondary">Seats in use</dt>
                     <dd class="col-6 mb-0">${number(mine.countingSeats ?? 0)} of ${number(allowed)}</dd>
                  </dl>
               </lia-card>
            </div>
            <div class="col-lg-6">
               <lia-card title="Run by its owners" icon="fa-solid fa-user-shield">
                  <p class="mb-0 text-body-secondary">
                     Members, repositories and billing are managed by the team's owners. Ask one of them
                     to make you an owner if that should be you.
                  </p>
               </lia-card>
            </div>
         </div>`;
   }
}

customElements.define('user-account-overview', UserAccountOverview);
